import { db, type OutboxEntry, type PendingMedia } from './localdb.js';

/**
 * The outbox.
 *
 * Every change a technician makes is written to the local database immediately and appended
 * here. The screen updates from local state at once — no request is awaited, so there is no
 * spinner and no failure path in the interaction itself.
 *
 * Four properties make this safe rather than merely convenient:
 *
 *   1. **Idempotency.** Each entry carries a key generated on the device. The server records
 *      keys it has seen and returns the original response on a repeat, so replaying after an
 *      ambiguous timeout — the request that may or may not have landed before signal died —
 *      cannot double-post a payment or duplicate a time entry.
 *   2. **Causal ordering.** `dependsOn` keeps linked work in order. A blocked entry does not
 *      block unrelated ones.
 *   3. **Bytes are not requests.** A capture is uploaded by its own resumable path, and an
 *      entry that needs one waits on `awaitsMedia` rather than carrying 400 KB through an
 *      ordered queue. When the upload finishes, the real attachment id is patched into the
 *      body — the client never invents one.
 *   4. **Nothing is discarded.** A permanently rejected entry moves to a visible "needs
 *      attention" list. It is never retried forever and never silently dropped.
 */

/** Retrying past this many attempts is not going to help; ask a human instead. */
const MAX_ATTEMPTS = 8;

/** Backoff schedule in milliseconds, indexed by attempt count. */
const BACKOFF = [0, 2_000, 5_000, 15_000, 60_000, 300_000, 900_000, 1_800_000];

export type EnqueueInput = Omit<
  OutboxEntry,
  'id' | 'idempotencyKey' | 'attempts' | 'lastAttemptAt' | 'rejectedReason' | 'createdAt'
>;

export async function enqueue(input: EnqueueInput): Promise<OutboxEntry> {
  const entry: OutboxEntry = {
    ...input,
    id: crypto.randomUUID(),
    idempotencyKey: crypto.randomUUID(),
    attempts: 0,
    lastAttemptAt: null,
    rejectedReason: null,
    createdAt: new Date().toISOString(),
  };
  await (await db()).put('outbox', entry);
  notify();
  return entry;
}

export async function pending(): Promise<OutboxEntry[]> {
  const all = await (await db()).getAllFromIndex('outbox', 'byCreated');
  return all.filter((e) => !e.rejectedReason);
}

/** Entries the server refused for a reason retrying cannot fix. These need a person. */
export async function rejected(): Promise<OutboxEntry[]> {
  const all = await (await db()).getAll('outbox');
  return all.filter((e) => e.rejectedReason);
}

/** Captures still on the phone, and the ones whose upload was refused. */
export async function pendingMedia(): Promise<PendingMedia[]> {
  const all = await (await db()).getAll('media');
  return all.filter((m) => !m.attachmentId && !m.rejectedReason);
}

export async function rejectedMedia(): Promise<PendingMedia[]> {
  const all = await (await db()).getAll('media');
  return all.filter((m) => m.rejectedReason);
}

/** What the status bar counts: everything that has not reached the office. */
export async function pendingCount(): Promise<number> {
  return (await pending()).length + (await pendingMedia()).length;
}

export async function discard(id: string): Promise<void> {
  await (await db()).delete('outbox', id);
  notify();
}

/** Put a rejected entry back in the queue after the person has fixed the cause. */
export async function retryRejected(id: string): Promise<void> {
  const database = await db();
  const entry = await database.get('outbox', id);
  if (entry) {
    await database.put('outbox', { ...entry, rejectedReason: null, attempts: 0, lastAttemptAt: null });
    notify();
    return;
  }
  const media = await database.get('media', id);
  if (media) {
    // Start the upload again from nothing: a refused one usually failed its checksum, and
    // resuming into the same server-side file would fail it again.
    await database.put('media', { ...media, rejectedReason: null, attempts: 0, lastAttemptAt: null, uploadId: null, offset: 0 });
    notify();
  }
}

let draining = false;

export type SendOutcome = { ok: true } | { ok: false; permanent: boolean; reason: string };

/**
 * Send everything queued, in order.
 *
 * Serial rather than parallel: ordering matters, and a technician's connection when it
 * returns is usually one bar of LTE in a stairwell — four concurrent uploads on that is
 * slower than one at a time, not faster.
 */
export async function drain(
  send: (entry: OutboxEntry) => Promise<SendOutcome>,
  /** Resolves the real attachment id for an entry waiting on a capture, or null if it is not up yet. */
  resolveMedia: (mediaId: string) => Promise<string | null>,
): Promise<{ sent: number; failed: number; rejected: number }> {
  if (draining) return { sent: 0, failed: 0, rejected: 0 };
  draining = true;

  const result = { sent: 0, failed: 0, rejected: 0 };
  try {
    const database = await db();
    const queue = await pending();
    const doneIds = new Set<string>();

    for (const entry of queue) {
      // Hold anything waiting on an entry that has not gone yet.
      if (entry.dependsOn && !doneIds.has(entry.dependsOn)) {
        const blocker = await database.get('outbox', entry.dependsOn);
        if (blocker) continue;
      }

      /*
       * Hold anything waiting on bytes.
       *
       * Not an error and not a failure — a photograph is simply slower than the sentence that
       * refers to it. When it lands, the server's own id is written into the body here, which
       * is the only place that can do it honestly.
       */
      let ready = entry;
      if (entry.awaitsMedia) {
        const attachmentId = await resolveMedia(entry.awaitsMedia.mediaId);
        if (!attachmentId) continue;
        ready = {
          ...entry,
          body: { ...(entry.body as Record<string, unknown>), [entry.awaitsMedia.field]: attachmentId },
          awaitsMedia: null,
        };
        await database.put('outbox', ready);
      }

      // Respect backoff so a failing entry does not hammer the network every time the
      // radio comes back.
      const wait = BACKOFF[Math.min(ready.attempts, BACKOFF.length - 1)] ?? 0;
      if (ready.lastAttemptAt && Date.now() - new Date(ready.lastAttemptAt).getTime() < wait) continue;

      const outcome = await send(ready);

      if (outcome.ok) {
        await database.delete('outbox', ready.id);
        doneIds.add(ready.id);
        result.sent += 1;
        continue;
      }

      const attempts = ready.attempts + 1;
      const givingUp = outcome.permanent || attempts >= MAX_ATTEMPTS;

      await database.put('outbox', {
        ...ready,
        attempts,
        lastAttemptAt: new Date().toISOString(),
        // A permanent refusal (validation, a conflict, a deleted job) is surfaced to the
        // technician. Exhausting retries is treated the same way: at that point silence
        // would be a lie.
        rejectedReason: givingUp ? outcome.reason || 'Le serveur a refusé cet envoi.' : null,
      });

      if (givingUp) result.rejected += 1;
      else result.failed += 1;
    }
  } finally {
    draining = false;
    notify();
  }
  return result;
}

// --- change notification --------------------------------------------------------------
// The status bar and the pending-work screen both need to react when the queue changes.
// A tiny subscription is cheaper and clearer than routing this through a query cache.

const listeners = new Set<() => void>();

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function notify(): void {
  for (const listener of listeners) listener();
}
